import { useActivation } from '@context/MobileOne';
import { Feedback, ScrollToTop } from '@core/index';
import { FeedbackImage } from '@core/FeedbackImage';
import { useA11y } from '@context/Utils/A11y';
import { useTitle } from '@context/Utils/Title';
import { appButtonType, appIconsPath, appRoute, appStorage } from '@utils/globalConstant';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAlphaComm } from '@dom-digital-online-media/dom-alphacomm-sdk';
import useCmsImage from '@utils/useCmsImage';

export function PaymentVerification() {
  // Contexts
  const { tA11yTranslate, getGlobalMetaTitles } = useA11y();
  const { announceTitle } = useTitle();
  const { errorAutoTopup, setErrorAutoTopup, setShowActivationHeader, setCurrentStep } =
    useActivation();
  const { onPaymentCommitOrder } = useAlphaComm();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [checkedOnError, setCheckedOnError] = useState(false);

  // Const
  const imageContent = useCmsImage('ek_reg_auto_top_up_suc_web_bg');
  let headerContent = tA11yTranslate('ek_reg_auto_top_feedback_suc _up_hdl1');
  let copyTextContent = tA11yTranslate('ek_reg_auto_top_feedback_suc _up_txt1');
  let buttonContent = tA11yTranslate('ek_reg_auto_top_feedback_suc_up_btn1');

  if (errorAutoTopup) {
    headerContent = tA11yTranslate('ek_reg_auto_top_up_feedback_err_hdl1');
    copyTextContent = tA11yTranslate('ek_reg_auto_top_up_feedback_err_txt1');
    buttonContent = tA11yTranslate('ek_reg_auto_top_up_feedback_err_btn1');
  }

  // META: page titles
  const { globalPageTitle, globalSuccessPageTitle, globalErrorPageTitle } = getGlobalMetaTitles();

  // Functions
  const handleButtonClick = () => {
    setCurrentStep(10);
    navigate(appRoute.ACTIVATION);
    setShowActivationHeader(false);
  };

  const orderVerification = async (params) => {
    try {
      const { data = {}, success } = await onPaymentCommitOrder(params);
      if (data && success) {
        const storedTopupAmount = await sessionStorage.getItem(appStorage.TOPUP_AMOUNT);
        setTopupAmount(storedTopupAmount);
        setErrorAutoTopup(false);
      } else {
        const storedPhoneNumber = await sessionStorage.getItem(appStorage.ACTIVATION_MSISDN);
        setPhoneNumber(storedPhoneNumber);
        setErrorAutoTopup(true);
      }
      setIsLoading(false);
      setCheckedOnError(true);
    } catch (error) {
      // BFSG: remove console.log
      // console.log(error);
      const storedPhoneNumber = await sessionStorage.getItem(appStorage.ACTIVATION_MSISDN);
      setPhoneNumber(storedPhoneNumber);
      setErrorAutoTopup(true);
      setIsLoading(false);
      setCheckedOnError(true);
    }
  };

  const readPhoneNumber = async () => {
    const storedPhoneNumber = await sessionStorage.getItem(appStorage.ACTIVATION_MSISDN);
    setPhoneNumber(storedPhoneNumber);
  }

  // Hooks
  useEffect(() => {
    const queryParamsData = {
      paymentToken: queryParams.get('paymentTokenId'),
      orderNumber: queryParams.get('orderNumber'),
      cartName: queryParams.get('cartName')
    };

    const paymentError = queryParams.get('paymentError');

    // BFSG: check parameter 'paymentError' in queryParams. If it exists and value is 'yes',
    // BFSG: directly show the error component without any order verification

    if ((queryParamsData.paymentToken || queryParamsData.orderNumber || queryParamsData.cartName) && (!paymentError || paymentError?.toLowerCase() !== 'yes')) {
      orderVerification(queryParamsData);
    } else {
      setIsLoading(false);
      readPhoneNumber();
      setErrorAutoTopup(true);
      setCheckedOnError(true);
    }
  }, []);

  useEffect(() => {
    if (checkedOnError) {
      if (errorAutoTopup) {
        announceTitle(`${globalPageTitle} - ${globalErrorPageTitle}`);
      } else {
        announceTitle(`${globalPageTitle} - ${globalSuccessPageTitle}`);
      }
    }
  }, [errorAutoTopup, checkedOnError]);

  // eslint-disable-next-line no-nested-ternary
  return isLoading ? (
    <>Loading..</>
  ) : errorAutoTopup ? (
    <>
      <ScrollToTop>
        <Feedback
          icon='ANGRY'
          headingContent={headerContent}
          copyTextContent={copyTextContent}
          buttonContent={buttonContent}
          buttonType={appButtonType.PRIMARY}
          buttonClick={handleButtonClick}
          replaceTitle="{NUMBER}"
          replaceTitleBy={phoneNumber}
          apiError={errorAutoTopup}
          showCard
        />
      </ScrollToTop>
    </>
  ) : (
    <>
      <ScrollToTop>
        <FeedbackImage
          image={imageContent}
          icon={appIconsPath.CELEBRATE}
          headingContent={headerContent}
          copyTextContent={copyTextContent}
          buttonContent={buttonContent}
          buttonType={appButtonType.PRIMARY}
          buttonIcon={appIconsPath.ARROW_RIGHT}
          buttonClick={handleButtonClick}
          replaceTitle="{AMOUNT}"
          replaceTitleBy={topupAmount}
        />
      </ScrollToTop>
    </>
  );
}

export default PaymentVerification;
